import {
  Component,
  ChangeDetectionStrategy,
  inject,
  ElementRef,
  viewChild,
  effect,
  computed,
  signal,
  ChangeDetectorRef,
} from '@angular/core';
import { DashboardService, SalesStat, PopularItem, WeekRange } from '../../services/dashboard.service';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { MaterialTableComponent } from '../shared/material-table/material-table.component';
import { SortableHeader } from '../../types/table';
import { MasterService } from '@/src/services/master.service';
import { M_STORE_BOARD } from '../../models/M_STORE_BOARD'
import { AuthenticationService } from '@/src/services/authentication.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Constants } from '@/src/helpers/constants';
import { LoadingService } from '../loading-ui/loading.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { M_STORE } from '@/src/models/M_STORE';
import { NoticeDetail } from '@/src/components/dashboard/notice-detail/notice-detail.component'

// Highcharts is loaded from a script tag in index.html, so it's a global variable.
declare var Highcharts: any;

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [TranslatePipe, MaterialTableComponent, TranslateModule, CommonModule, NoticeDetail],
  templateUrl: './dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent {
  dashboardService = inject(DashboardService);
  masterService = inject(MasterService)
  auth = inject(AuthenticationService)
  translate = inject(TranslateService)

  salesStats = signal<SalesStat[]>([]);
  storeSummary = signal(null);
  popularServices = signal<PopularItem[]>([])
  popularOptions = signal<PopularItem[]>([])
  reservationStats = signal(null);
  notices = signal<M_STORE_BOARD[]>([])
  param = {
    P_HQ_CD: '',
    P_STR_CD: '',
    P_TYPE: 'SD',
    P_START_DT: '',
    P_END_DT: ''
  }

  weeklySales!: WeekRange;
  weekPopular!: WeekRange;
  weekReservation!: WeekRange;

  noticeHeaders = computed<SortableHeader[]>(() => {
    // This makes the headers reactive to language changes
    return [
      // { key: 'toggle', label: '', sortable: false },
      { key: 'id', label: this.translate.instant('DASHBRHQ0004'), sortable: true },
      { key: 'title', label: this.translate.instant('DASHBRHQ0005'), sortable: true },
      { key: 'content', label: this.translate.instant('DASHBRHQ0006'), sortable: true },
    ];
  });
  expandedRow: any | null = 0;

  toggleRow(id: any) {
    this.expandedRow = this.expandedRow === id ? 0 : id;
  }

  chartContainer = viewChild<ElementRef>('salesChart');

  ngOnInit() {
    const now = new Date();
    this.weeklySales = this.dashboardService.getWeekByDate(now);
    this.weekPopular = this.dashboardService.getWeekByDate(now);
    this.weekReservation = this.dashboardService.getWeekByDate(now);
    this.getStoreInfo();
    this.getDashboard();
    this.getBoard();
  }

  constructor(private cdr: ChangeDetectorRef, private loadingService: LoadingService, private sanitizer: DomSanitizer) {
    effect((onCleanup) => {
      const chartElement = this.chartContainer()?.nativeElement;
      const salesData = this.salesStats();

      if (!chartElement || salesData.length === 0) {
        return;
      }

      let timeoutId: any = null;
      const checkAndRender = () => {
        // Poll until the Highcharts library is loaded from the external script.
        if (typeof Highcharts !== 'undefined' && typeof Highcharts.chart === 'function') {
          this.createHighchartsChart(chartElement, salesData);
        } else {
          timeoutId = setTimeout(checkAndRender, 100);
        }
      };

      checkAndRender();

      onCleanup(() => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      });
    });
  }

  getBoard() {
    this.param.P_HQ_CD = this.auth.getHQCD();
    this.param.P_STR_CD = this.auth.getStrCd();
    //SP_GET_BOARD_LIST
    this.masterService.excute_sp('6a83983f-298d-11f1-9014-00155d001e02', this.param).subscribe((res: any) => {
      if (res?.isOK) {
        this.notices.set(new M_STORE_BOARD().getAll(res?.result?.dataRes[0]?.rows));
        this.notices().forEach(n => this.setSafeContent(n));
      }
    })
  }

  loading: boolean = false;
  openNoticeDetail = signal(false);
  selectedNotice = new M_STORE_BOARD();
  getDetailst(notice: any) {
    const param = {
      P_HQ_CD: this.param.P_HQ_CD,
      P_BOARD_SEQ: notice.boarD_SEQ
    }
    // if (this.expandedRow === notice.boarD_SEQ) {
    //   this.toggleRow(notice.boarD_SEQ);
    // }
    // else {

    // }
    this.loading = true;
    //SP_GET_BOARD_FILE_LIST
    this.masterService.excute_sp('6a839788-298d-11f1-9014-00155d001e02', param).subscribe(async (res: any) => {
      if (res?.isOK) {
        let uploadedFiles = new Array();
        for (let f of res?.result?.dataRes[0]?.rows) {
          try {
            const filename = f.filE_PATH.split('/').pop()!;
            const ext = filename.split('.').pop()?.toLowerCase();

            const kind = this.getFileKind(filename);

            const blob = await fetch(f.filE_PATH).then(r => r.blob());
            const file = new File([blob], filename, { type: blob.type });

            const objectURL = URL.createObjectURL(file);

            uploadedFiles.push({
              file,
              kind,
              previewUrl: f.filE_PATH,
              objectURL,
              name: filename,
            });
          }
          catch { }

        }
        notice.files = uploadedFiles;
        this.loading = false;
        this.toggleRow(notice.boarD_SEQ);
        this.cdr.detectChanges();
      }
      else {
        this.loading = false;
        this.cdr.detectChanges();
      }
      this.loading = false;
      this.selectedNotice = notice;
      this.openNoticeDetail.set(true);
      this.cdr.detectChanges();
    })
  }

  cleanName(name: string) {
    return name.replace(/_\d{14}(?=\.)/, '');
  }

  getFileKind(filename: string) {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext!)) return 'image';
    if (['mp4', 'mov', 'avi'].includes(ext!)) return 'video';
    if (ext === 'pdf') return 'pdf';
    if (['doc', 'docx'].includes(ext!)) return 'docx';
    if (ext === 'txt') return 'txt';
    if (['xls', 'xlsx'].includes(ext!)) return 'excel';
    return 'other';
  }
  review(item: any) {
    switch (item.kind) {
      case 'image':
      case 'video':
        window.open(item.previewUrl, '_blank');
        break;

      case 'pdf':
        window.open(item.previewUrl, '_blank');
        break;

      case 'docx':
      case 'excel':
      case 'txt':
        this.downloadFile(item);
        break;

      default:
        this.downloadFile(item);
        break;
    }
  }

  async downloadFile(item: any) {
    const response = await fetch(item.previewUrl);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = item.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  }

  private createHighchartsChart(element: HTMLElement, data: SalesStat[]) {
    if (!element || !data || data.length === 0) {
      return;
    }

    const isKorean = Constants.getLang() === 'ko';

    Highcharts.chart(element, {
      chart: {
        type: 'column',
        backgroundColor: 'transparent',
        height: 250,
      },
      title: {
        text: null,
      },
      xAxis: {
        categories: data.map((d: SalesStat) => d.salE_DT),
        labels: {
          style: {
            color: '#6b7280',
            fontSize: '12px',
          },
          formatter: function (): string {
            const value = (this as any).value?.toString();

            if (value && value.length === 8) {
              const year = value.substring(0, 4);
              const month = value.substring(4, 6);
              const day = value.substring(6, 8);

              return isKorean
                ? `${month}월<br/>${day}일`
                : `${month}/${day}`;
            }

            return value || '';
          },
          useHTML: true,
        },
        lineColor: '#e5e7eb',
        tickColor: 'transparent',
      },
      yAxis: {
        min: 0,
        tickInterval: 1000,
        title: {
          text: null,
        },
        labels: {
          enabled: true,
          style: {
            color: '#6b7280',
            fontSize: '12px',
          },
        },
        gridLineColor: '#e5e7eb',
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        shared: true,
        headerFormat: '<b>{point.date}</b><br/>',
        pointFormat: '<span style="color:{series.color}">●</span> {series.name}: <b>{point.y}</b><br/>',
      },
      plotOptions: {
        column: {
          borderWidth: 0,
          borderRadius: 0,
          pointPadding: 0.2,
          groupPadding: 0.1,
        },
      },
      series: [
        {
          name: this.translate.instant('DASHBSTR0018'),
          data: data.map((d: SalesStat) => ({
            y: d.paY_CARD,
            date: this.masterService.formatDateStringYYYYMMDD(d.salE_DT)
          })),
          color: '#f87171'
        },
        {
          name: this.translate.instant('DASHBSTR0017'),
          data: data.map((d: SalesStat) => ({
            y: d.paY_CASH,
            date: this.masterService.formatDateStringYYYYMMDD(d.salE_DT)
          })),
          color: '#60a5fa'
        },
        {
          name: this.translate.instant('DASHBSTR0019'),
          data: data.map((d: SalesStat) => ({
            y: d.paY_OTHER,
            date: this.masterService.formatDateStringYYYYMMDD(d.salE_DT)
          })),
          color: '#fbbf24'
        },
      ],
      credits: {
        enabled: false,
      },
    });
  }

  safeContentMap = new Map<number, SafeHtml>();

  setSafeContent(notice: any) {
    const safe = this.sanitizer.bypassSecurityTrustHtml(notice.boarD_CONTENTS);
    this.safeContentMap.set(notice.boarD_SEQ, safe);
  }

  storeInfo = new M_STORE();

  getStoreInfo() {
    const param = {
      P_HQ_CD: this.auth.getHQCD(),
      P_STR_CD: this.auth.getStrCd()
    }
    //SP_STR_GET_INFO
    this.masterService.excute_sp('6a839b98-298d-11f1-9014-00155d001e02', param).subscribe((res: any) => {
      if (res?.isOK) {
        const data = new M_STORE().getStore(res?.result?.dataRes[0]?.rows[0])
        this.storeInfo = data;
      }
    })
  }

  getDashboard() {
    const param = {
      P_HQ_CD: this.auth.getHQCD(),
      P_STR_CD: this.auth.getStrCd(),
      P_STARTDT: this.masterService.formatDateYYYYMMDD4(this.weeklySales.start),
      P_ENDDT: this.masterService.formatDateYYYYMMDD4(this.weeklySales.end)
    }

    this.masterService.excute_sp('65553ad1-33e1-11f1-9014-00155d001e02', param).subscribe((res: any) => {
      if (res?.isOK) {
        const data = res?.result?.dataRes;
        this.storeSummary.set(data[0]?.rows[0]);
        this.salesStats.set(this.fillMissingDates(data[1]?.rows, this.weeklySales.start, this.weeklySales.end))
        this.popularServices.set(data[2]?.rows)
        this.popularOptions.set(data[3]?.rows)
        this.reservationStats.set(data[4]?.rows[0])
      }
    })
  }

  changeWeek(
    type: 'sales' | 'popular' | 'reservation',
    action: 'next' | 'prev'
  ) {
    const map = {
      sales: 'weeklySales',
      popular: 'weekPopular',
      reservation: 'weekReservation'
    } as const;

    const key = map[type];

    this[key] =
      action === 'next'
        ? this.dashboardService.nextWeek(this[key])
        : this.dashboardService.prevWeek(this[key]);

    const param = {
      P_HQ_CD: this.auth.getHQCD(),
      P_STR_CD: this.auth.getStrCd(),
      P_STARTDT: this.masterService.formatDateYYYYMMDD4(this[key].start),
      P_ENDDT: this.masterService.formatDateYYYYMMDD4(this[key].end),
    };

    //SP_GET_STORE_DASHBOARD_WEEKLYSALES, SP_GET_STORE_DASHBOARD_POPULAR, SP_GET_STORE_DASHBOARD_WEEKLYRESERVATIONS
    const sp = type === 'sales' ? '48d0edff-3496-11f1-9014-00155d001e02' : type === 'popular' ? '3553cae7-3496-11f1-9014-00155d001e02' : '3f40b20c-3496-11f1-9014-00155d001e02';
    this.masterService.excute_sp(sp, param).subscribe((res: any) => {
      if (res?.isOK) {
        const data = res?.result?.dataRes;
        if (type === 'sales') this.salesStats.set(this.fillMissingDates(data[0]?.rows, this.weeklySales.start, this.weeklySales.end));
        else if (type === 'popular') {
          this.popularServices.set(data[0]?.rows)
          this.popularOptions.set(data[1]?.rows)
        }
        else
          this.reservationStats.set(data[0]?.rows[0]);
      }
    })
  }

  fillMissingDates(data: any[], start: Date, end: Date): any[] {
    const map = new Map<string, any>();

    data.forEach(item => {
      map.set(item.salE_DT, item);
    });

    const result: any[] = [];

    const current = new Date(start);

    while (current <= end) {
      const key = this.masterService.formatDateYYYYMMDD4(current);

      if (map.has(key)) {
        result.push(map.get(key));
      } else {
        result.push({
          salE_DT: key,
          paY_AMT: 0,
          paY_CARD: 0,
          paY_CASH: 0,
          paY_OTHER: 0
        });
      }

      current.setDate(current.getDate() + 1);
    }

    return result;
  }
}
